import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CaseReader() {
  const { caseId } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('case');

  useEffect(() => {
    fetchCase();
    
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };
    
    const handleKeyDown = (e) => {
      // Block save (Ctrl+S) and copy/cut shortcuts; allow Ctrl+P for printing
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'c' || e.key === 'x' || e.key === 'a')) {
        e.preventDefault();
        return false;
      }
    };
    
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [caseId]);

  const fetchCase = async () => {
    try {
      const response = await axios.get(`${API}/cases/${caseId}`);
      setCaseData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Please subscribe to access this case.');
        navigate('/subscription');
      } else {
        toast.error('Failed to load case');
        navigate('/cases');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  // Handle null case data (e.g., access denied)
  if (!caseData) {
    return null;
  }

  const { case: caseStudy, teaching_note, worksheet } = caseData;

  return (
    <div className="min-h-screen bg-white">
      <nav className="fixed top-0 w-full z-50 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <Link to="/cases" className="flex items-center gap-2 text-slate-700 hover:text-slate-900" data-testid="back-to-library-link">
              <ArrowLeft className="w-5 h-5" />
              Back to Library
            </Link>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-slate-400" data-testid="protection-notice">
                <Download className="w-4 h-4" />
                <span className="text-xs">Download disabled</span>
              </div>
              <button
                onClick={() => window.print()}
                className="flex items-center gap-2 text-slate-700 hover:text-slate-900 transition-colors"
                data-testid="print-button"
                title="Print this case"
              >
                <Printer className="w-4 h-4" />
                <span className="text-xs">Print</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-20 pb-12">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-8">
            <div className="flex gap-2 mb-4">
              <span className="inline-block px-3 py-1 bg-slate-100 text-slate-700 text-xs font-mono rounded">
                {Array.isArray(caseStudy.domain) ? caseStudy.domain.join(', ') : caseStudy.domain}
              </span>
              <span className="inline-block px-3 py-1 bg-slate-100 text-slate-700 text-xs font-mono rounded">
                {caseStudy.geography}
              </span>
              <span className="inline-block px-3 py-1 bg-slate-100 text-slate-700 text-xs font-mono rounded">
                {caseStudy.year}
              </span>
            </div>
            <h1 className="text-5xl font-serif font-bold tracking-tight text-slate-900 mb-4" data-testid="case-title">
              {caseStudy.title}
            </h1>
            <p className="text-xl text-slate-600">{caseStudy.description}</p>
          </div>

          <div className="border-b border-slate-200 mb-8">
            <div className="flex gap-8">
              <button
                onClick={() => setActiveTab('case')}
                className={`pb-4 border-b-2 transition-colors ${
                  activeTab === 'case' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
                }`}
                data-testid="tab-case-study"
              >
                Case Study
              </button>
              {teaching_note && (user?.role === 'instructor' || user?.role === 'admin' || user?.role === 'institution_admin') && (
                <button
                  onClick={() => setActiveTab('teaching')}
                  className={`pb-4 border-b-2 transition-colors ${
                    activeTab === 'teaching' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
                  }`}
                  data-testid="tab-teaching-notes"
                >
                  Teaching Notes
                </button>
              )}
              {worksheet && (
                <button
                  onClick={() => setActiveTab('worksheet')}
                  className={`pb-4 border-b-2 transition-colors ${
                    activeTab === 'worksheet' ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500'
                  }`}
                  data-testid="tab-worksheet"
                >
                  Student Worksheet (Read-only)
                </button>
              )}
            </div>
          </div>

          {worksheet && (
            <div className="mb-6">
              <Link
                to={`/worksheet/${caseId}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded hover:bg-orange-700 transition-all font-medium"
                data-testid="interactive-worksheet-button"
              >
                📝 Open Interactive Worksheet
              </Link>
              <p className="text-sm text-slate-600 mt-2">
                Work on the worksheet online, save your progress, and download when complete
              </p>
            </div>
          )}

          <div className="prose prose-slate max-w-none content-protected" data-testid="case-content">
            {activeTab === 'case' && (
              <div className="text-justify leading-relaxed text-slate-700 case-content-styled" dangerouslySetInnerHTML={{ __html: caseStudy.content }} />
            )}
            {activeTab === 'teaching' && teaching_note && (
              <div className="text-justify leading-relaxed text-slate-700 case-content-styled" dangerouslySetInnerHTML={{ __html: teaching_note.content }} />
            )}
            {activeTab === 'worksheet' && worksheet && (
              <div className="text-justify leading-relaxed text-slate-700 case-content-styled" dangerouslySetInnerHTML={{ __html: worksheet.content }} />
            )}
          </div>

          <div className="mt-12 p-6 bg-slate-50 rounded border border-slate-200" data-testid="user-watermark">
            <p className="text-sm text-slate-500 text-center">
              This content is licensed to {user.email} | {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}